import clsx from 'clsx';
import type * as React from 'react';

import type { InlineExpressionData } from '@gitbook/doc-core';
import { formatExpressionResult } from '@gitbook/expr';
import type { InlineProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';

import { useEvaluateExpression } from '../../Expressions';
import { ExpressionPopoverEditor } from '../adaptive';
import { VoidInlineWithPopover } from '../common';

/**
 * Render and evaluate an inline expression
 */
export function InlineExpression(props: InlineProps): React.ReactElement {
    const { node, editor } = props;

    const { expression } = node.data.toFrozenObject() as InlineExpressionData;
    const result = useEvaluateExpression(expression);

    return (
        <VoidInlineWithPopover
            {...props}
            renderSpan={() =>
                result.error ? (
                    <span className={clsx('rounded px-1', expression ? 'bg-danger' : 'bg-warning')}>
                        <Icon.Expression
                            className={clsx(
                                'size-[1em]',
                                expression ? 'text-danger' : 'text-warning'
                            )}
                        />
                        {expression ? <Icon.Alert className="ml-1 size-[1em] text-danger" /> : null}
                    </span>
                ) : (
                    `${formatExpressionResult(result.value, '(empty string)')}`
                )
            }
            popover={
                <ExpressionPopoverEditor
                    node={node}
                    editor={editor}
                    title={{
                        editable: 'Edit expression',
                        readOnly: 'Expression',
                    }}
                    documentationURL={{ path: 'creating-content/formatting/inline#expressions' }}
                />
            }
            hoverHighlighting="inline"
        />
    );
}
